import {ChangeDetectionStrategy, Component, input, Signal, signal} from "@angular/core";
import {initDebugger} from "felles-ng2/støttefunksjoner/debug";
import {FormControl, FormGroup, ReactiveFormsModule, Validators} from "@angular/forms";
import {MatFormField, MatHint, MatLabel} from "@angular/material/form-field";
import {MatOption, MatSelect} from "@angular/material/select";
import {TranslateModule} from "@ngx-translate/core";
import {MatSlideToggle} from "@angular/material/slide-toggle";
import {MatTooltip} from "@angular/material/tooltip";
import {takeUntilDestroyed, toObservable, toSignal} from "@angular/core/rxjs-interop";
import {filter, startWith, switchMap} from "rxjs";
import {erIkkeNil} from "felles-ng2/støttefunksjoner/nilsjekk";
import {RiktekstRedigeringKomponent} from "web-ng2/delt/komponenter/skjema/riktekst-redigering/riktekst-redigering";
import {map} from "rxjs/operators";
import {Klokke} from "felles-ng2/tjenester/Klokke";
import {Temporal} from "temporal-polyfill";
import PlainDate = Temporal.PlainDate;
import {FormaterPlainDatePipe} from "felles-ng2/pipes/FormaterPlainDatePipe";

const debug = initDebugger('SpørreundersøkelseInvitasjonSkjemaKomponent');

export type SpørreundersøkelseInvitasjonSkjemaType = ReturnType<typeof SpørreundersøkelseInvitasjonSkjemaKomponent.opprettSkjema>;
export type SpørreundersøkelseInvitasjonSkjemaVerdi = SpørreundersøkelseInvitasjonSkjemaType['value'];

/**
 * Skjema for å sende invitasjon til besvarelse av spørreundersøkelse
 */
@Component({
    selector: 'spoerreundersoekelse-invitasjon-skjema',
    templateUrl: 'spørreundersøkelse-invitasjon-skjema.html',
    styleUrl: 'spørreundersøkelse-invitasjon-skjema.scss',
    imports: [
        MatFormField,
        MatLabel,
        MatOption,
        TranslateModule,
        ReactiveFormsModule,
        MatSelect,
        MatSlideToggle,
        MatTooltip,
        RiktekstRedigeringKomponent,
        MatHint,
        FormaterPlainDatePipe
    ],
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class SpørreundersøkelseInvitasjonSkjemaKomponent {

    static opprettSkjema() {
        return new FormGroup({
            sendInvitasjon: new FormControl(true, {nonNullable: true, validators: Validators.required}),
            /** Undersøkelsens varighet, målt i antall dager fra dagens dato */
            varighet: new FormControl(7, {nonNullable: true, validators: Validators.required}),
            beskjed: new FormControl('', {nonNullable: true})
        });
    }

    skjemakontroll = input.required<SpørreundersøkelseInvitasjonSkjemaType>();
    antallRespondenter = input(0);

    varigheter = signal([1, 2, 3, 5, 7, 14, 30]);
    periodeSluttDato: Signal<PlainDate | null>;

    constructor(klokke: Klokke) {
        const varighetVerdi = toObservable(this.skjemakontroll)
          .pipe(switchMap(skjema => skjema.controls.varighet.valueChanges.pipe(startWith(this.skjemakontroll().value.varighet))));

        //sørger for at verdien for varighet angitt i skjemakontroll er tilgjengelig i nedtrekksmenyen
        varighetVerdi.pipe(
          filter(varighet => erIkkeNil(varighet)),
          filter(varighet => varighet > 0),
          filter(varighet => !this.varigheter().includes(varighet)),
          takeUntilDestroyed())
          .subscribe(varighet => this.varigheter.update(varigheter => [varighet, ...varigheter]));

        this.periodeSluttDato = toSignal(varighetVerdi.pipe(map(varighet =>
          erIkkeNil(varighet) ? klokke.nåSomPlainDate().add({days: varighet}) : null)), {initialValue: null});
    }
}
